/**
 * Accessibility Utilities
 * 
 * Utility functions for improving accessibility in quality control components
 */

/**
 * Generates a unique ID for accessibility purposes
 */
export const generateAccessibilityId = (prefix: string): string => {
  return `${prefix}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Checks if an element is focusable
 */
export const isFocusable = (element: HTMLElement): boolean => {
  const focusableSelectors = [
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'a[href]',
    '[tabindex]:not([tabindex="-1"])',
    '[contenteditable="true"]',
  ];

  return focusableSelectors.some(selector => element.matches(selector));
};

/**
 * Gets all focusable elements within a container
 */
export const getFocusableElements = (container: HTMLElement): HTMLElement[] => {
  const focusableSelectors = [
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'a[href]',
    '[tabindex]:not([tabindex="-1"])',
    '[contenteditable="true"]',
  ].join(', ');

  return Array.from(container.querySelectorAll(focusableSelectors));
};

/**
 * Moves focus to the next focusable element
 */
export const focusNextElement = (currentElement: HTMLElement, container?: HTMLElement): void => {
  const focusableElements = getFocusableElements(container || document.body);
  const currentIndex = focusableElements.indexOf(currentElement);
  
  if (currentIndex !== -1 && currentIndex < focusableElements.length - 1) {
    focusableElements[currentIndex + 1].focus();
  } else if (focusableElements.length > 0) {
    focusableElements[0].focus(); // Wrap to first element
  }
};

/**
 * Moves focus to the previous focusable element
 */
export const focusPreviousElement = (currentElement: HTMLElement, container?: HTMLElement): void => {
  const focusableElements = getFocusableElements(container || document.body);
  const currentIndex = focusableElements.indexOf(currentElement);
  
  if (currentIndex > 0) {
    focusableElements[currentIndex - 1].focus();
  } else if (focusableElements.length > 0) {
    focusableElements[focusableElements.length - 1].focus(); // Wrap to last element
  }
};

/**
 * Creates an accessible description for a quality warning
 */
export const createWarningDescription = (warning: {
  stationCode: string;
  stationName: string;
  warningLevel: number;
  parameter: string;
  errorCode: string;
  measuredValue: number;
  timestamp: string;
}): string => {
  const levelText = getWarningLevelText(warning.warningLevel);
  const date = new Date(warning.timestamp).toLocaleString();
  
  return `${levelText} quality warning for station ${warning.stationCode} ${warning.stationName}. ` +
         `Parameter ${warning.parameter} with value ${warning.measuredValue} failed check ${warning.errorCode} ` +
         `at ${date}.`;
};

/**
 * Gets accessible text for warning levels
 */
export const getWarningLevelText = (level: number): string => {
  switch (level) {
    case 1:
      return 'Critical level 1';
    case 2:
      return 'Moderate level 2';
    case 3:
      return 'Minor level 3';
    default:
      return 'Unknown level';
  }
};

/**
 * Creates accessible sort button labels
 */
export const createSortButtonLabel = (
  field: string,
  currentField: string,
  direction: 'asc' | 'desc'
): string => {
  const isActive = field === currentField;
  const baseLabel = `Sort by ${field}`;
  
  if (isActive) {
    const directionText = direction === 'asc' ? 'ascending' : 'descending';
    return `${baseLabel} ${directionText}`;
  }
  
  return baseLabel;
};

/**
 * Announces content changes to screen readers
 */
export const announceToScreenReader = (
  message: string,
  priority: 'polite' | 'assertive' = 'polite'
): void => {
  const announcement = document.createElement('div');
  announcement.setAttribute('aria-live', priority);
  announcement.setAttribute('aria-atomic', 'true');
  announcement.className = 'sr-only';
  announcement.style.position = 'absolute';
  announcement.style.left = '-10000px';
  announcement.style.width = '1px';
  announcement.style.height = '1px';
  announcement.style.overflow = 'hidden';
  
  document.body.appendChild(announcement);
  
  // Set message after a brief delay to ensure it's announced
  setTimeout(() => {
    announcement.textContent = message;
    
    // Remove after announcement
    setTimeout(() => {
      if (announcement.parentNode) {
        announcement.parentNode.removeChild(announcement);
      }
    }, 1000);
  }, 100);
};

/**
 * Checks if user prefers reduced motion
 */
export const prefersReducedMotion = (): boolean => {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

/**
 * Checks if user prefers high contrast
 */
export const prefersHighContrast = (): boolean => {
  return window.matchMedia('(prefers-contrast: high)').matches;
};

/**
 * Gets the appropriate animation duration based on user preferences
 */
export const getAnimationDuration = (defaultDuration: number): number => {
  return prefersReducedMotion() ? 0 : defaultDuration;
};

/**
 * Creates accessible table headers with sort functionality
 */
export const createAccessibleTableHeader = (
  label: string,
  sortable: boolean = false,
  sortDirection?: 'asc' | 'desc' | 'none'
): {
  role: string;
  'aria-sort'?: string;
  tabIndex?: number;
} => {
  const attributes: any = {
    role: 'columnheader',
  };

  if (sortable) {
    attributes.tabIndex = 0;
    attributes['aria-sort'] = sortDirection || 'none';
  }

  return attributes;
};

/**
 * Creates accessible progress announcements
 */
export const createProgressAnnouncement = (
  current: number,
  total: number,
  itemType: string = 'items'
): string => {
  const percentage = Math.round((current / total) * 100);
  return `Loading ${itemType}: ${current} of ${total} (${percentage}% complete)`;
};

/**
 * Creates accessible error messages
 */
export const createAccessibleErrorMessage = (
  error: string,
  context?: string
): string => {
  const prefix = context ? `Error in ${context}: ` : 'Error: ';
  return `${prefix}${error}. Please try again or contact support if the problem persists.`;
};

/**
 * Creates accessible success messages
 */
export const createAccessibleSuccessMessage = (
  action: string,
  details?: string
): string => {
  const message = `${action} completed successfully.`;
  return details ? `${message} ${details}` : message;
};

/**
 * Validates color contrast ratio
 */
export const validateColorContrast = (
  foreground: string,
  background: string,
  level: 'AA' | 'AAA' = 'AA'
): boolean => {
  // This is a simplified implementation
  // In a real application, you would use a proper color contrast library
  const requiredRatio = level === 'AAA' ? 7 : 4.5;
  
  // Convert colors to RGB and calculate luminance
  // This is a placeholder - implement proper contrast calculation
  return true; // Placeholder return
};

/**
 * Creates accessible landmark labels
 */
export const createLandmarkLabel = (
  type: 'main' | 'navigation' | 'search' | 'banner' | 'contentinfo',
  context?: string
): string => {
  const labels = {
    main: 'Main content',
    navigation: 'Navigation',
    search: 'Search',
    banner: 'Site header',
    contentinfo: 'Site footer',
  };
  
  const baseLabel = labels[type];
  return context ? `${baseLabel} - ${context}` : baseLabel;
};

/**
 * Creates accessible form field descriptions
 */
export const createFieldDescription = (
  fieldName: string,
  required: boolean = false,
  format?: string
): string => {
  let description = `Enter ${fieldName}`;
  
  if (required) {
    description += ' (required)';
  }
  
  if (format) {
    description += `. Format: ${format}`;
  }
  
  return description;
};

/**
 * Creates accessible loading states
 */
export const createLoadingState = (
  action: string,
  progress?: number
): {
  'aria-busy': boolean;
  'aria-live': string;
  'aria-label': string;
} => {
  let label = `Loading ${action}`;
  
  if (progress !== undefined) {
    label += ` (${Math.round(progress)}% complete)`;
  }
  
  return {
    'aria-busy': true,
    'aria-live': 'polite',
    'aria-label': label,
  };
};

/**
 * Manages focus restoration after modal/dialog interactions
 */
export class FocusManager {
  private previousFocus: HTMLElement | null = null;

  saveFocus(): void {
    this.previousFocus = document.activeElement as HTMLElement;
  }

  restoreFocus(): void {
    if (this.previousFocus && this.previousFocus.focus) {
      this.previousFocus.focus();
    }
  }

  trapFocus(container: HTMLElement): () => void {
    const focusableElements = getFocusableElements(container);
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;

      if (event.shiftKey) {
        if (document.activeElement === firstElement) {
          event.preventDefault();
          lastElement?.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          event.preventDefault();
          firstElement?.focus();
        }
      }
    };

    container.addEventListener('keydown', handleKeyDown);
    firstElement?.focus();

    return () => {
      container.removeEventListener('keydown', handleKeyDown);
    };
  }
}